import React, { useEffect, useState } from "react";
import { commitProductImport, parseProductXLSX, previewProductImport } from "../logic/apiCalls";
import InventoryToast from "./InventoryToast";

function InventoryImportModal({ file, onClose, onCommitted }) {
  const [stage, setStage] = useState("validating");
  const [products, setProducts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      try {
        const parsed = await parseProductXLSX(file);
        const response = await previewProductImport(parsed);
        if (cancelled) return;
        setProducts(parsed);
        setPreview(response);
        setStage("review");
      } catch (validationError) {
        if (!cancelled) { setError(validationError.message); setStage("error"); }
      }
    }
    validate();
    return () => { cancelled = true; };
  }, [file]);

  const commit = async () => {
    setStage("committing");
    setError("");
    try {
      const response = await commitProductImport(products);
      setResult(response.summary);
      setStage("done");
      onCommitted();
    } catch (commitError) {
      setError(commitError.message);
      setStage("review");
    }
  };

  const hasErrors = Number(preview?.summary?.errors || 0) > 0;

  return (
    <div className="inventory-modal-overlay" role="presentation">
      <section className="inventory-import-modal" role="dialog" aria-modal="true" aria-labelledby="inventory-import-title">
        <header><div><span>Importación segura</span><h2 id="inventory-import-title">Revisar archivo XLSX</h2><p>{file.name}</p></div><button type="button" className="inventory-icon-button is-light" aria-label="Cerrar" onClick={onClose} disabled={stage === "committing"}><span className="material-symbols-outlined">close</span></button></header>
        <div className="inventory-import-steps" aria-label="Progreso"><span className="is-complete">1 · Archivo</span><span className={stage !== "validating" ? "is-complete" : "is-active"}>2 · Validación</span><span className={stage === "done" ? "is-complete" : stage === "committing" ? "is-active" : ""}>3 · Confirmación</span></div>
        <div className="inventory-import-modal__body">
          {stage === "validating" ? <div className="inventory-state"><span className="inventory-spinner" /> Validando todas las filas sin modificar inventario…</div> : null}
          {stage === "error" ? <InventoryToast type="error" message={error} onClose={() => setError("")} /> : null}
          {stage === "review" || stage === "committing" ? <>
            {error ? <InventoryToast type="error" message={error} onClose={() => setError("")} /> : null}
            <div className="inventory-import-summary"><div><strong>{preview.summary.total}</strong><span>Filas</span></div><div className="is-success"><strong>{preview.summary.inserted}</strong><span>Nuevos</span></div><div className="is-info"><strong>{preview.summary.updated}</strong><span>Actualizaciones</span></div><div className={hasErrors ? "is-error" : ""}><strong>{preview.summary.errors}</strong><span>Errores</span></div></div>
            <div className="inventory-import-table-wrap"><table className="inventory-import-table"><thead><tr><th>Fila</th><th>Código</th><th>Ubicación</th><th>Acción</th><th>Stock</th><th>Observación</th></tr></thead><tbody>{preview.rows.slice(0, 300).map((row) => <tr key={row.row} className={row.action === "error" ? "is-error" : ""}><td>{row.row}</td><td>{row.code || "—"}</td><td>{row.location || "—"}</td><td>{row.action === "insert" ? "Crear" : row.action === "update" ? "Actualizar" : "Corregir"}</td><td>{row.nextQty === null ? "Sin cambio" : `${row.currentQty ?? 0} → ${row.nextQty}`}</td><td>{row.errors?.join(" ") || "Lista para importar"}</td></tr>)}</tbody></table></div>
            {preview.rows.length > 300 ? <p className="inventory-import-note">Se muestran las primeras 300 filas. Todas fueron validadas.</p> : null}
          </> : null}
          {stage === "done" ? <div className="inventory-import-done"><span className="material-symbols-outlined">task_alt</span><h3>Importación completada</h3><p>Se crearon <strong>{result.inserted_products}</strong> productos y se actualizaron <strong>{result.updated_products}</strong>. Todos los cambios se confirmaron en una sola transacción.</p></div> : null}
        </div>
        <footer><p>{hasErrors ? "Corrige el archivo antes de importar. No se realizó ningún cambio." : "Los cambios se aplicarán juntos y quedarán registrados en el historial."}</p><div><button type="button" className="inventory-secondary-button" onClick={onClose} disabled={stage === "committing"}>{stage === "done" ? "Cerrar" : "Cancelar"}</button>{stage === "review" ? <button type="button" className="inventory-primary-button" onClick={commit} disabled={hasErrors}>Confirmar importación</button> : null}</div></footer>
      </section>
    </div>
  );
}

export default InventoryImportModal;
